import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// material
import {
  useMediaQuery,
  Box,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  TextField
} from '@mui/material';
// redux
import qs from 'qs';
import { setCurrentBrand, getBrandList } from '../../redux/slices/brand';
// components
import MenuPopover from '../../components/MenuPopover';
import { MIconButton } from '../../components/@material-extend';

const ICON_PATH = '/static/icons/navbar/ic_map.svg';

// ----------------------------------------------------------------------

const calcWidth = (brandOptions) => {
  // const defaultMax = 16;
  const defaultMax = 11;
  let width = 200;
  let lengthMax = 0;
  brandOptions.forEach((item) => {
    if (item.length > lengthMax) lengthMax = item.length;
  });

  if (defaultMax < lengthMax) width += (lengthMax - defaultMax) * 8;
  return width;
};

// ----------------------------------------------------------------------

export default function LanguagePopover() {
  const navigate = useNavigate();
  const location = useLocation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const brand = useSelector((state) => state.brand);
  const isWideEnough = useMediaQuery('(min-width:600px)');
  const [menuWidth, setMenuWidth] = useState();
  const [barndSearchKey, setBrandSearchKey] = useState('');

  /* Update list of available brands */
  useEffect(() => {
    if (brand.availableBrands.length === 0) {
      dispatch(getBrandList());
    }
    if (brand.availableBrands.length > 0)
      setMenuWidth(calcWidth(brand.availableBrands));
  }, [dispatch, brand.availableBrands]);

  const handleBrandChange = (brandName) => {
    dispatch(setCurrentBrand(brandName));
    let changedToListView = false;
    // If edit or view page change to list
    if (location.pathname) {
      if (
        location.pathname.indexOf('/dashboard/inventory/edit') > -1 ||
        location.pathname.indexOf('/dashboard/inventory/view') > -1
      )
        location.pathname = '/dashboard/inventory/list';
      changedToListView = true;
    }
    /* on change of brand update the url */
    if (location.search) {
      const params = qs.parse(location.search.replace('?', ''));
      /* if params has 'brand' and it's different then reload the page with new brand name */
      if (Object.keys(params).includes('brand') && params.brand !== brandName) {
        console.debug('changing brand in url path');
        let newPath = `${location.pathname}?`;
        // If edit or view page change to list
        if (changedToListView) {
          newPath += `brand=${brandName}&itemsAmount=100&itemsOffset=0&status=In-Stock&`;
        } else {
          Object.keys(params).forEach((key) => {
            if (key === 'brand') newPath += `brand=${brandName}&`;
            else newPath += `${key}=${params[key]}&`;
          });
        }

        newPath = newPath.substring(0, newPath.length - 1); // get rid of the last '&'
        navigate(newPath);
      }
    }
  };

  return (
    <>
      <Typography
        sx={{ color: 'black', cursor: 'pointer' }}
        onClick={() => setOpen(true)}
      >
        {brand.availableBrandsDisplayNames?.find(
          (b) => b.name === brand.currentBrand
        )?.brandDisplayName || brand.currentBrand}
      </Typography>

      <MIconButton
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && { bgcolor: 'action.selected' })
        }}
      >
        <img src={ICON_PATH} alt={brand.currentBrand} />
      </MIconButton>
      {!brand.isLoading && (
        <MenuPopover
          open={open}
          onClose={() => setOpen(false)}
          anchorEl={anchorRef.current}
          sx={{ width: `${menuWidth}px` }}
        >
          <TextField
            fullWidth
            placeholder="Search by brand name"
            value={barndSearchKey}
            onChange={(e) => setBrandSearchKey(e.target.value)}
            sx={{ p: 2 }}
          />
          <Box
            sx={{
              py: 1,
              maxHeight: `60vh`,
              overflow: 'scroll',
              overflowX: 'hidden'
            }}
          >
            {brand.availableBrandsDisplayNames
              .filter((brandName) => {
                if (barndSearchKey) {
                  return (
                    brandName.brandDisplayName?.match(
                      RegExp(barndSearchKey, 'i')
                    ) != null
                  );
                }
                return true;
              })
              .sort((a, b) =>
                a.brandDisplayName
                  ?.toLowerCase()
                  .localeCompare(b.brandDisplayName?.toLowerCase())
              )
              .map((brandName) => (
                <MenuItem
                  key={brandName.name}
                  selected={brandName.name === brand.currentBrand}
                  onClick={() => {
                    handleBrandChange(brandName.name);
                    setOpen(false);
                  }}
                  sx={{ py: 1, px: 2.5 }}
                >
                  <ListItemIcon>
                    <Box component="img" alt={brandName.name} src={ICON_PATH} />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                    {brandName.brandDisplayName || ''}
                  </ListItemText>
                </MenuItem>
              ))}
          </Box>
        </MenuPopover>
      )}
      {brand.isLoading && (
        <MenuPopover
          open={open}
          onClose={() => setOpen(false)}
          anchorEl={anchorRef.current}
          sx={{ width: `${menuWidth}px` }}
        >
          <MenuItem
            onClick={() => {
              setOpen(false);
            }}
            sx={{ py: 1, px: 2.5 }}
          >
            <Box sx={{ py: 1 }}>
              <ListItemText>
                <Typography>Loading Brands ...</Typography>
              </ListItemText>
            </Box>
          </MenuItem>
        </MenuPopover>
      )}
    </>
  );
}
